import { LoadingOutlined, MessageOutlined } from "@ant-design/icons";
import { Col, Input, Row, Typography } from "antd";
import { useSession } from "contexts/SessionContext";
import { useTranslation } from "contexts/TranslationContext";
import { Formik } from "formik";
import useAuthHook from "hooks/useAuthHook";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useInitiateMobileChangeMutation,
  useSignInMutation,
  useVerifyMobileChangeMutation,
  useVerifyMutation,
} from "services/auth";
import { Button } from "shared/components/Button";
import * as LOCAL_STORAGE_KEYS from "shared/constants/localStorageKeys";
import routes, { appRoute } from "shared/constants/routes";
import { Header } from "shared/views/Header";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";
import gaTrackingEvent from "../utilities/gaTrackingEvent";
import getGeoLocation from "../utilities/getGeoLocation";
import { Label } from "Components";
import HelpSupport from "assets/svg/chat.svg";
import Call from "assets/svg/call.svg";
import ChangeNumber from "assets/svg/change-number.svg";
import PhoneSuccess from "assets/svg/success-phone.svg";
import BackArrow from "assets/icons/backarrow.svg";
import Email from "assets/svg/email.svg";
import BgBottomLeftIcon from "assets/svg/bgBottomLeft.svg";
import BgTopRightIcon from "assets/svg/bgTopRight.svg";
import LoginImgEn from "assets/svg/LoginImgEn.png";
import LoginImgAr from "assets/svg/LoginImgAr.png";

const { Text } = Typography;

export const HelpAndContact = () => {
  const { translate } = useTranslation();
  return (
    <div>
      {/* <div className="flex items-center justify-start sm:justify-center flex-row gap-2 align-middle mt-4">
        <img src={HelpSupport} alt="help" className="bg-[#F3EFE1] rounded-lg p-2" />
        <Link to={routes.AUTH.COMPLAINTS} style={{ fontSize: '14px', color: '#666666', fontWeight: '700' }}>
          {translate('SIGN_IN.HELP_AND_SUPPORT')}
        </Link>
      </div> */}

      <div className="mt-6 flex flex-col sm:flex-row gap-2 items-start sm:items-center sm:justify-center p-2 sm:bg-[#F3EFE1] rounded-lg">
        <div className="flex flex-row items-center">
          <img src={Call} alt="call" className="rounded-lg p-2" />
          <a
            href="tel:920005477"
            className="text-xs ml-2 font-semibold hover:underline text-dark"
          >
            {translate("SIGN_IN.HELP_LINE_CALL")}
          </a>
        </div>

        {/* <div className="hidden sm:flex flex-row items-center">
          <p className="text-xs text-black">{translate("SIGN_IN.OR")}</p>
        </div> */}

        <div className="flex flex-row items-center">
          <img src={Email} alt="email" className="rounded-lg p-2" />
          <p className="text-xs ml-2 font-semibold text-dark">
            {translate("SIGN_IN.HELP_LINE_EMAIL")}
          </p>
        </div>
      </div>
    </div>
  );
};

const schema = Yup.object().shape({
  iqamaId: Yup.string().length(10, "ID_10_DIGITS").required("CANNOT_BE_EMPTY"),
  password: Yup.string().required("CANNOT_BE_EMPTY"),
});

const SignInForm = ({ setStep, setId, onChangeNumber }) => {
  const [signIn, { isLoading }] = useSignInMutation();
  const { translate } = useTranslation();

  const initialValues = {
    iqamaId: "",
    password: "",
  };

  // Device ID handling
  let deviceId = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_ID, deviceId);
  }

  const onSubmit = async (values, { resetForm }) => {
    //gaTrackingEvent('login', {})
    try {
      const response = await signIn({
        ...values,
        deviceId,
      }).unwrap();
      if (response.isOTPSend) {
        setId(values.iqamaId);
        setStep("verification");
        toast.success(translate("SIGN_IN.CREDENTIAL_SUCCESS"));
      } else {
        resetForm();
        toast.error(
          response?.message || translate("SIGN_IN.INVALID_CREDENTIALS")
        );
      }
    } catch (error) {
      if (error?.data?.code) {
        switch (error?.data?.code) {
          case "INVALID_CREDENTIALS":
          case "ACCOUNT_DELETED":
          case "DEVICE_REGISTRATION_LIMIT_REACHED":
          case "IVR_NOT_VERIFIED":
          case "DEVICE_NOT_REGISTERED":
          case "LOGIN_FAILED":
            toast.error(translate(`SIGN_IN.${error?.data?.code}`));
            break;
          case "OTP_SEND_FAILED":
            toast.error(translate(`SIGN_IN.TIMEOUT`));
            break;
          case "ACCOUNT_LOCKED":
            toast.error(error?.data?.message);
            break;
          default:
            toast.error(translate("SIGN_IN.SOMETHING_WENT_WRONG"));
            break;
        }
      } else toast.error(translate("SIGN_IN.SOMETHING_WENT_WRONG"));
    }
  };

  const gaSignUpEvent = () => {
    gaTrackingEvent("create_account", {});
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors }) => (
        <form onSubmit={handleSubmit} className="space-y-3 md:space-y-3">
          <div>
            <Label
              label={translate("SIGN_IN.FIELDS.ID")}
              required={true}
              name={translate("SIGN_IN.FIELDS.ID")}
            />
            <Input
              className={"tamweel-base-input"}
              name="iqamaId"
              id="iqamaId"
              placeholder={translate("SIGN_IN.FIELDS.ID")}
              maxLength={10}
              onChange={(e) => {
                // Remove any character that is not an English letter or number
                const value = e.target.value.replace(
                  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669]/g,
                  ""
                );
                handleChange({
                  target: {
                    name: "iqamaId",
                    value: value,
                  },
                });
              }}
              value={values.iqamaId}
            />
            {errors.iqamaId && (
              <Text type="danger">
                {translate("SIGN_IN." + errors.iqamaId)}
              </Text>
            )}
          </div>
          <div>
            <Label
              label={translate("SIGN_IN.FIELDS.PASSWORD")}
              required={true}
              name={translate("SIGN_IN.FIELDS.PASSWORD")}
            />
            <Input.Password
              className={"tamweel-base-input"}
              status={errors.password ? "error" : null}
              name="password"
              id="password"
              placeholder={translate("SIGN_IN.FIELDS.PASSWORD")}
              onChange={(e) => {
                const value = e.target.value.replace(
                  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669]/g,
                  ""
                );
                handleChange({
                  target: {
                    name: "password",
                    value: value,
                  },
                });
              }}
              value={values.password}
            />
            {errors.password && (
              <Text type="danger">
                {translate("SIGN_IN." + errors.password)}
              </Text>
            )}
          </div>
          <div className="text-center  text-sm">
            <Link
              style={{
                fontSize: "14px",
                letterSpacing: 0.3,
                fontWeight: "600",
              }}
              to={routes.AUTH.FORGOT_PASSWORD}
            >
              {translate("SIGN_IN.FORGOT_PASSWORD")}
            </Link>
          </div>
          <div
            style={{ fontSize: "14.5px" }}
            className="uppercase text-center text-sm text-[#989898]"
          >
            <span>{translate("SIGN_IN.DONT_HAVE_AN_ACCOUNT")} </span>
            <Link
              className="text-primary"
              to={routes.AUTH.SIGN_UP}
              onClick={gaSignUpEvent}
            >
              {translate("SIGN_IN.SIGN_UP")}
            </Link>
          </div>
          <Button
            style={{color: "#232222"}}
            className={"tamweel-button font-bold mb-4"}
            type="submit"
            disabled={isLoading}
            block
          >
            {isLoading ? <LoadingOutlined /> : translate("SIGN_IN.LOGIN")}
          </Button>
          <div className="flex justify-center items-center">
            <button
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#f3f3f3] transition"
              onClick={onChangeNumber}
            >
              <div className="bg-[#F3EFE1] rounded-xl flex items-center justify-center">
                <img
                  src={ChangeNumber}
                  alt="change-number"
                  className="rounded-lg p-2"
                />
              </div>
              <span className="text-gray-500 text-base font-medium">
                {translate("SIGN_IN.CHANGE_MOBILE_NUMBER")}
              </span>
            </button>
          </div>
        </form>
      )}
    </Formik>
  );
};

const ChangeMobileNumber = ({ setStep, setMobileChangeData }) => {
  const { translate, language } = useTranslation();
  const isRTL = language.code === "ar";

  const [initiateMobileChange, { isLoading }] =
    useInitiateMobileChangeMutation();

  const validationSchema = Yup.object().shape({
    iqamaId: Yup.string()
      .matches(/^[0-9]{10}$/, translate("SIGN_IN.INVALID_NATIONAL_ID"))
      .required(translate("SIGN_IN.NATIONAL_ID_REQUIRED")),
    oldMobile: Yup.string()
      .matches(/^[0-9]{9}$/, translate("SIGN_IN.INVALID_OLD_MOBILE"))
      .required(translate("SIGN_IN.OLD_MOBILE_REQUIRED")),
    newMobile: Yup.string()
      .matches(/^[0-9]{9}$/, translate("SIGN_IN.INVALID_NEW_MOBILE"))
      .required(translate("SIGN_IN.NEW_MOBILE_REQUIRED")),
    // .notOneOf([Yup.ref("oldMobile")], translate("SIGN_IN.MOBILE_SAME_ERROR")),
  });

  const initialValues = {
    iqamaId: "",
    oldMobile: "",
    newMobile: "",
  };

  const onSubmit = async (values, { resetForm }) => {
    try {
      const response = await initiateMobileChange(values).unwrap();
      toast.success(translate("SIGN_IN.CHANGE_MOBILE_SUCCESS"));
      setMobileChangeData(values);
      setStep?.("otp");
      // resetForm();
    } catch (error) {
      const errorMessage =
        error?.data?.error || // backend "error" field
        error?.data?.message || // fallback if API returns "message"
        translate("SIGN_IN.SOMETHING_WENT_WRONG");

      toast.error(errorMessage);
    }
  };

  return (
    <div className="flex justify-center items-center">
      <div className="bg-white shadow-sm rounded-2xl p-8 w-full max-w-md border border-gray-100">
        {/* Header and Form */}
        {/* Header */}
        <div className="mb-4">
          <div className="flex">
            <button
              className={`bg-[#F3EFE1] rounded-xl flex items-center justify-center ${
                isRTL ? "ml-5" : "mr-5"
              }`}
              onClick={() => setStep?.("form")}
              type="button"
            >
              <img
                src={BackArrow}
                alt="back-button"
                className={`rounded-lg p-2 transition-transform duration-300 ${
                  isRTL ? "rotate-180" : ""
                }`}
              />
            </button>
            <div>
              <h2 className="text-[#007382] font-semibold text-lg uppercase tracking-wide">
                {translate("SIGN_IN.CHANGE_MOBILE_NUMBER")}
              </h2>
              <div className="h-1 w-10 bg-yellow-400 rounded mt-1"></div>
            </div>
          </div>
          <p className="text-gray-500 text-sm mt-2">
            {translate("SIGN_IN.CHANGE_MOBILE_DESC")}
          </p>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, handleChange, values, errors, touched }) => (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* National ID */}
              <div>
                <Label label={translate("SIGN_IN.NATIONAL_ID")} required />
                <Input
                  name="iqamaId"
                  placeholder={translate("SIGN_IN.NATIONAL_ID_PLACEHOLDER")}
                  value={values.iqamaId}
                  onChange={handleChange}
                />
                {touched.iqamaId && errors.iqamaId && (
                  <p className="text-red-500 text-xs mt-1">{errors.iqamaId}</p>
                )}
              </div>

              {/* Old Mobile */}
              <div>
                <Label
                  label={translate("SIGN_IN.OLD_MOBILE_NUMBER")}
                  required
                />
                <div className="flex items-center rounded-lg border border-gray-300 bg-[#F2F2F2]">
                  <span className="px-3 text-gray-500">+966</span>
                  <Input
                    className="border-none focus:ring-0 flex-1"
                    name="oldMobile"
                    placeholder={translate("SIGN_IN.MOBILE_NUMBER_PLACEHOLDER")}
                    value={values.oldMobile}
                    onChange={handleChange}
                  />
                </div>
                {touched.oldMobile && errors.oldMobile && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.oldMobile}
                  </p>
                )}
              </div>

              {/* New Mobile */}
              <div>
                <Label
                  label={translate("SIGN_IN.NEW_MOBILE_NUMBER")}
                  required
                />
                <div className="flex items-center rounded-lg border border-gray-300 bg-[#F2F2F2]">
                  <span className="px-3 text-gray-500">+966</span>
                  <Input
                    className="border-none focus:ring-0 flex-1"
                    name="newMobile"
                    placeholder={translate("SIGN_IN.MOBILE_NUMBER_PLACEHOLDER")}
                    value={values.newMobile}
                    onChange={handleChange}
                  />
                </div>
                {touched.newMobile && errors.newMobile && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.newMobile}
                  </p>
                )}
              </div>

              {/* Submit */}
              <Button
                style={{ color: "#232222" }}
                className="tamweel-button w-full mb-4"
                type="submit"
                disabled={isLoading}
              >
                {isLoading
                  ? translate("SIGN_IN.PROCESSING")
                  : translate("SIGN_IN.SUBMIT")}
              </Button>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

const OtpVerification = ({ setStep, formValues }) => {
  const { translate, language } = useTranslation();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timer, setTimer] = useState(59);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  // RTK Query hooks
  const [initiateMobileChange, { isLoading: isResending }] =
    useInitiateMobileChangeMutation();
  const [verifyMobileChange, { isLoading: isVerifying }] =
    useVerifyMobileChangeMutation();

  const isRTL = language.code === "ar";

  // Countdown logic
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    } else {
      setIsResendDisabled(false);
    }
  }, [timer]);

  const handleChange = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 3) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };

  // Handle OTP verification using mutation
  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");

    if (otpValue.length !== 4) {
      toast.error(
        translate("SIGN_IN.INVALID_OTP") || "Please enter a valid OTP"
      );
      return;
    }

    if (!formValues) {
      toast.error("Missing mobile change details.");
      return;
    }

    try {
      const payload = {
        ...formValues,
        otp: otpValue,
      };

      const res = await verifyMobileChange(payload).unwrap();

      toast.success(
        translate("SIGN_IN.OTP_VERIFIED") || "OTP verified successfully!"
      );

      setStep("success");
    } catch (error) {
      toast.error(error?.data?.message || "Verification failed");
    }
  };

  // ✅ Resend OTP using initiate mutation
  const handleResend = async () => {
    if (!formValues) {
      toast.error("Missing mobile change details.");
      return;
    }

    try {
      await initiateMobileChange({
        iqamaId: formValues.iqamaId,
        newMobile: formValues.newMobile,
        oldMobile: formValues.oldMobile,
      }).unwrap();

      toast.success(
        translate("SIGN_IN.OTP_RESENT") || "OTP has been resent successfully!"
      );

      setOtp(["", "", "", ""]);
      setTimer(59);
      setIsResendDisabled(true);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to resend OTP");
    }
  };

  return (
    <div className="flex justify-center items-center">
      <div className="bg-white shadow-sm rounded-2xl p-8 w-full max-w-lg border border-gray-100 text-center">
        {/* Header */}
        <div className="mb-6 text-left">
          <div className="flex">
            <button
              className={`bg-[#F3EFE1] rounded-xl flex items-center justify-center ${
                isRTL ? "ml-5" : "mr-5"
              }`}
              onClick={() => setStep?.("changeNumber")}
              type="button"
            >
              <img
                src={BackArrow}
                alt="back-button"
                className={`rounded-lg p-2 transition-transform duration-300 ${
                  isRTL ? "rotate-180" : ""
                }`}
              />
            </button>
            <div>
              <h2 className="text-[#007382] font-semibold text-lg uppercase tracking-wide">
                {translate("SIGN_IN.CHANGE_MOBILE_NUMBER")}
              </h2>
              <div className="h-1 w-10 bg-yellow-400 rounded mt-1"></div>
            </div>
          </div>

          <p className="text-gray-500 text-sm mt-2">
            {translate("SIGN_IN.OTP_DESC") ||
              "We sent your one-time password (OTP) by SMS"}
          </p>
        </div>

        {/* OTP Input */}
        <form onSubmit={handleSubmit}>
          <div className="flex justify-center gap-3 mb-6">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                className="w-20 h-12 text-center text-lg border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 outline-none"
              />
            ))}
          </div>

          {/* Timer + Resend */}
          <div className="text-gray-500 font-medium mb-6">
            {timer > 0 ? (
              <p className="text-lg">00:{timer.toString().padStart(2, "0")}</p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={isResendDisabled || isResending}
                className={`font-medium ${
                  isResending
                    ? "opacity-50 cursor-not-allowed"
                    : "text-[#AFAFAF] hover:text-gray-600"
                }`}
              >
                {isResending
                  ? translate("SIGN_IN.SENDING") || "Sending..."
                  : translate("SIGN_IN.RESEND_CODE") || "Resend Code"}
              </button>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isVerifying}
            style={{ color: "#232222" }}
            className={"tamweel-button w-full mb-4 font-semibold"}
          >
            {isVerifying
              ? translate("SIGN_IN.VERIFYING") || "Please wait..."
              : translate("SIGN_IN.SUBMIT") || "Submit"}
          </Button>
        </form>

        {/* Support Info */}
        <HelpAndContact />
      </div>
    </div>
  );
};

const SuccessScreen = ({ setStep }) => {
  const { translate } = useTranslation();
  return (
    <div className="flex items-center justify-center">
      <div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white shadow-md rounded-2xl p-8 w-full max-w-md text-center border border-gray-100"
      >
        {/* Illustration */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <img
              src={PhoneSuccess}
              alt="Success Illustration"
              width={160}
              height={160}
            />
          </div>
        </div>

        {/* Text */}
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          {translate("SIGN_IN.MOBILE_NUMBER_UPDATED")} <br />{" "}
          {translate("SIGN_IN.SUCCESSFULLY")}
        </h2>

        {/* Button */}
        <Button
          onClick={() => setStep?.("form")}
          style={{ color: "#232222" }}
          className={"tamweel-button w-full mb-4 font-semibold"}
          // className="bg-yellow-400 text-black font-semibold py-2 px-6 rounded-lg mt-4 hover:bg-yellow-500 transition w-full"
        >
          {translate("COMPLAINTS.BACK")}
        </Button>
      </div>
    </div>
  );
};

const SignInVerification = ({ setStep, id }) => {
  const [seconds, setSeconds] = useState(60);
  const [otp, setOtp] = useState(["", "", "", ""]);

  const { signIn } = useSession();

  // Device ID handling
  let deviceId = localStorage.getItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(LOCAL_STORAGE_KEYS.DEVICE_ID, deviceId);
  }

  const otpRef1 = useRef(null);
  const otpRef2 = useRef(null);
  const otpRef3 = useRef(null);
  const otpRef4 = useRef(null);

  const [verify, { isLoading }] = useVerifyMutation();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { language } = useTranslation();

  const countdown = (intervalId) => {
    if (seconds <= 0) {
      setStep("form");
      toast.error(translate("SIGN_IN.TIMEOUT"));
      clearInterval(intervalId);
    } else {
      setSeconds(seconds - 1);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      countdown(intervalId);
    }, 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [seconds]); // eslint-disable-line

  const handleChange = (value, index) => {
    setOtp((otp) => otp.map((_, i) => (index === i ? value : _)));

    switch (index) {
      case 0:
        otpRef2.current.focus();
        break;
      case 1:
        otpRef3.current.focus();
        break;
      case 2:
        otpRef4.current.focus();
        break;
      default:
        return;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const { latitude, longitude } = await getGeoLocation();

    try {
      const response = await verify({
        otp: otp.join(""),
        iqamaId: id,
        deviceId,
        ObjLatLng: {
          lat: latitude ?? "",
          lang: longitude ?? "",
        },
      }).unwrap();

      if (response?.verified) {
        signIn(response?.user);
        navigate(appRoute(routes.HOME)); //redirect to home page and not pre-steps now
        console.log(response, "response");
        let userObj = response.user;
        userObj = {
          ...userObj,
          token: response.token,
          upgrade: response.upgrade
        };

        localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(userObj));
        toast.success(translate("SIGN_IN.VERIFICATION_COMPLETE"));
      } else {
        toast.error(translate("SIGN_IN.WRONG_OTP"));
        setStep("form");
      }
    } catch (error) {
      toast.error(translate("SIGN_IN.SOMETHING_WENT_WRONG"));
    }
  };

  return (
    <>
      <div className="text-gray-400">{translate("SIGN_IN.WE_HAVE_SENT")}</div>

      <form onSubmit={onSubmit} className="space-y-6 mt-6">
        <div className="flex justify-between w-full max-w-xl mx-auto" dir="ltr">
          {[otpRef1, otpRef2, otpRef3, otpRef4].map((ref, index) => (
            <input
              dir="ltr"
              key={index}
              ref={ref}
              maxLength={1}
              type="text"
              pattern="[0-9]*"
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d$/.test(value)) {
                  handleChange(value, index);
                } else {
                  e.target.value = "";
                }
              }}
              onKeyDown={(e) => {
                if (
                  !/^\d$/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "Tab" &&
                  e.key !== "ArrowLeft" &&
                  e.key !== "ArrowRight"
                ) {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => e.target.blur()} // disables mouse wheel input
              onPaste={(e) => e.preventDefault()} // disables pasting
              name={`otp${index + 1}`}
              id={`otp${index + 1}`}
              className="text-2xl font-semibold border border-gray-300 rounded-lg w-[22%] h-16 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 text-center no-spinner"
              required
            />
          ))}
        </div>

        <div className="text-center text-gray-400 font-bold text-3xl">
          00:{String(seconds).padStart(2, "0")}
        </div>

        <div className="text-center text-gray-400 font-bold text-sm cursor-not-allowed">
          {translate("SIGN_IN.RESEND_CODE")}
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          block
          className="bg-yellow-400 hover:bg-yellow-500 text-white font-semibold text-base py-2 rounded-lg shadow"
        >
          {isLoading ? <LoadingOutlined /> : translate("SIGN_IN.SEND")}
        </Button>
      </form>
    </>
  );
};

export default function SignIn() {
  const [step, setStep] = useState("form");
  const [id, setId] = useState(null);
  const { logout } = useAuthHook();
  const { translate, language } = useTranslation();
  const [mobileChangeData, setMobileChangeData] = useState(null);
  const isRtl = language?.dir === "rtl";
  const loginHeroImg = isRtl ? LoginImgAr : LoginImgEn;

  useEffect(() => {
    logout();
  }, []);

  useEffect(() => {
    // Create script element
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.innerHTML = `
      (function(w,d,s,r,k,h,m){
          if(w.performance && w.performance.timing && w.performance.navigation) {
              w[r] = w[r] || function(){(w[r].q = w[r].q || []).push(arguments)};
              h=d.createElement('script');h.async=true;h.setAttribute('src',s+k);
              d.getElementsByTagName('head')[0].appendChild(h);
              (m = window.onerror),(window.onerror = function (b, c, d, f, g) {
                  m && m(b, c, d, f, g),g || (g = new Error(b)),(w[r].q = w[r].q || []).push(["captureException",g]);
              });
          }
      })(window,document,'https://192.168.130.82:7443/RUMScript.js?appKey=','apmr','LQdPsgHljiGhGSyIefsP7i1R5s9LXjtY10030009');
    `;

    // Append script to head
    document.head.appendChild(script);

    // Cleanup on component unmount
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section className="relative h-screen bg-[#F0EFEF] overflow-hidden">
      <img
        src={BgTopRightIcon}
        alt=""
        aria-hidden
        className="absolute top-0 ltr:right-0 rtl:left-0 rtl:scale-x-[-1] w-[260px] sm:w-[300px] md:w-[240px] lg:w-[250px] xl:w-[290px] pointer-events-none select-none z-0"
      />

      {/* Bottom-left decoration */}
      <img
        src={BgBottomLeftIcon}
        alt=""
        aria-hidden
        className="absolute bottom-0 ltr:left-0 rtl:right-0 rtl:scale-x-[-1] w-[260px] sm:w-[300px] md:w-[240px] lg:w-[250px] xl:w-[290px] pointer-events-none select-none z-0"
      />

      {/* Hero — bleeds to viewport edge, shown on form + verification steps, md and up.
          Separate EN/AR artwork (each with its own baked-in text) swapped by
          language, rather than mirroring a single image — no rtl:scale-x flip. */}
      {step !== "changeNumber" && step !== "otp" && step !== "success" && (
        <img
          src={loginHeroImg}
          alt=""
          aria-hidden
          className="hidden [@media(min-width:991px)_and_(min-height:651px)]:block absolute top-1/2 -translate-y-1/2 ltr:right-0 rtl:left-0 h-[min(70vh,600px)] w-auto pointer-events-none select-none z-0"
        />
      )}

      <Header />
      <div className="tamweel-container relative z-10 flex flex-col h-full pb-10">
        {" "}
        {/* Scrollable only if content exceeds screen height */}
        <div className="flex-1 overflow-auto  [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden mt-12-">
          <div className="flex flex-col items-center [@media(min-width:991px)_and_(min-height:651px)]:items-start justify-center min-h-full w-full">
            {/* Login Form Section */}
            <div className="w-full max-w-2xl [@media(min-width:991px)_and_(min-height:651px)]:max-w-xl">
              <div className="rounded-3xl px-8 py-8 md:px-4 md:py-10 lg:px-8 lg:py-8 xl:px-12 xl:py-12">
                {step !== "changeNumber" &&
                  step !== "otp" &&
                  step !== "success" && (
                    <>
                      <h1 className="form-header-text tamweel-text-22 font-bold leading-tight tracking-tight text-primary uppercase">
                        {translate("SIGN_IN.TITLE")}
                      </h1>

                      <div className="bg-secondary w-9 h-1" />

                      <div className="form-header-title-wrapper">
                        <h2 className="uppercase text-black text-2xl sm:text-3xl font-bold my-6">
                          {translate("SIGN_IN.DESCRIPTION")}
                        </h2>
                      </div>
                    </>
                  )}
                {step === "changeNumber" && (
                  <ChangeMobileNumber
                    setStep={setStep}
                    setMobileChangeData={setMobileChangeData}
                  />
                )}
                {step === "otp" && (
                  <OtpVerification
                    setStep={setStep}
                    formValues={mobileChangeData}
                  />
                )}
                {step === "success" && <SuccessScreen setStep={setStep} />}
                {step === "form" && (
                  <SignInForm
                    setId={setId}
                    setStep={setStep}
                    onChangeNumber={() => setStep("changeNumber")}
                  />
                )}
                {step === "verification" && (
                  <SignInVerification id={id} setStep={setStep} />
                )}

                {step !== "changeNumber" &&
                  step !== "otp" &&
                  step !== "success" && <HelpAndContact />}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
